import { Launch } from '@mui/icons-material';
import { Autocomplete, Box, TextField } from '@mui/material';
import _ from 'lodash';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDebouncedValue } from '../../../hooks/useDebounce';
import { ROUTE_PATHS } from '../../../routes/Routes';
import { useGetAllEmployees } from '../../../services/react-query/employees';
import { Employee, EmployeeRestricted } from '../../../types/Employee';
import { Loading } from '../../../uikit';
import { ErrorObject } from '../../../utility/inputUtils';
import usePermissionAllowed from '../../../hooks/usePermissionAllowed';
import { Actions } from '../../../services/react-query/permissions';
import { AutocompleteValue } from '@mui/material/useAutocomplete';

export type EmployeeOption = Employee | EmployeeRestricted;

interface EmployeeSearchAutocompleteProps<Multiple extends boolean | undefined = false, T extends EmployeeOption = EmployeeOption> {
  value: Multiple extends true ? T[] : T | null;
  onChange: (value: Multiple extends true ? T[] : T | null) => void;
  isMultiple?: Multiple;
  error?: ErrorObject;
  label?: string;
  excludedEmployees?: string[];
  isDisabled?: boolean;
}

export default function EmployeeSearchAutocomplete<Multiple extends boolean | undefined = false, T extends EmployeeOption = EmployeeOption>({
  value,
  onChange,
  isMultiple,
  error,
  label,
  excludedEmployees = [],
  isDisabled = false,
}: EmployeeSearchAutocompleteProps<Multiple, T>) {
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebouncedValue(searchInput, 300);

  const canReadEmployee = usePermissionAllowed(Actions.EMPLOYEES_READ);
  const isEmployeeReadAllowed = canReadEmployee === true;

  const { data: employeesData, isFetching } = useGetAllEmployees({
    params: { search: debouncedSearchInput },
    enabled: !!debouncedSearchInput,
  });

  const employees: EmployeeOption[] = _.reject(employeesData?.data ?? [], (emp) => _.includes(excludedEmployees, emp.uuid));

  const autocompleteValue = (isMultiple ? (value ?? []) : value) as AutocompleteValue<EmployeeOption, Multiple, false, false>;

  return (
    <Box sx={{ display: 'inline-flex', gap: 1, width: '100%' }}>
      <Autocomplete<EmployeeOption, Multiple, false, false>
        fullWidth
        multiple={isMultiple}
        size="small"
        loading={isFetching}
        loadingText={<Loading size={22} />}
        options={employees}
        noOptionsText={debouncedSearchInput ? 'No employees found' : 'Search for employees'}
        disabled={isDisabled}
        value={autocompleteValue}
        onChange={(_, newValue) => onChange(newValue as Multiple extends true ? T[] : T | null)}
        getOptionLabel={(emp) => `${emp.display_name} – ${emp.email}`}
        renderInput={(params) => <TextField {...params} {...error} label={label || 'Employee'} size="small" onChange={(e) => setSearchInput(e.target.value)} />}
      />

      {!isMultiple &&
        !Array.isArray(value) &&
        value &&
        (isEmployeeReadAllowed ? (
          <Link to={`/${ROUTE_PATHS.EMPLOYEES}/${value.uuid}`} style={{ alignContent: 'center' }}>
            <Launch sx={{ color: 'black' }} />
          </Link>
        ) : (
          <Launch
            sx={{
              color: 'grey.400',
              cursor: 'not-allowed',
              alignSelf: 'center',
            }}
          />
        ))}
    </Box>
  );
}
